import React, { useEffect, useRef, useState } from "react";
import { Check, Loader2, MapPin, Search, X } from "lucide-react";

import useGoogleLocationApi from "../Services/useGoogleLocationApi";

let googleMapsLoadingPromise = null;

// =========================================================
// LOAD GOOGLE MAPS
// =========================================================

const loadGoogleMaps = (apiKey) => {
  if (window.google?.maps) {
    return Promise.resolve(window.google.maps);
  }

  if (!apiKey) {
    return Promise.reject(new Error("Google Maps API key is missing."));
  }

  if (googleMapsLoadingPromise) {
    return googleMapsLoadingPromise;
  }

  googleMapsLoadingPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(
      'script[data-chaafo-google-maps="true"]',
    );

    if (existingScript) {
      existingScript.addEventListener(
        "load",
        () => {
          resolve(window.google?.maps);
        },
        {
          once: true,
        },
      );

      existingScript.addEventListener(
        "error",
        () => {
          reject(new Error("Unable to load Google Maps."));
        },
        {
          once: true,
        },
      );

      return;
    }

    const script = document.createElement("script");

    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey,
    )}&v=weekly`;

    script.async = true;
    script.defer = true;

    script.dataset.chaafoGoogleMaps = "true";

    script.onload = () => {
      if (!window.google?.maps) {
        reject(new Error("Google Maps could not be initialized."));

        return;
      }

      resolve(window.google.maps);
    };

    script.onerror = () => {
      reject(new Error("Unable to load Google Maps."));
    };

    document.head.appendChild(script);
  });

  return googleMapsLoadingPromise;
};

// =========================================================
// VALID COORDINATE
// =========================================================

const getValidCoordinate = (value, fallbackValue) => {
  const coordinate = Number(value);

  if (Number.isFinite(coordinate)) {
    return coordinate;
  }

  return fallbackValue;
};

// =========================================================
// NORMALIZE STRING
// =========================================================

const normalizeString = (value) => String(value ?? "").trim();

// =========================================================
// EXTRACT GOOGLE PLACE SUGGESTIONS
// =========================================================

const extractPlaceSuggestions = (response) => {
  const source =
    response?.response ??
    response?.data ??
    response?.predictions ??
    response?.suggestions ??
    [];

  if (!Array.isArray(source)) {
    return [];
  }

  return source
    .map((item) => {
      const placeId = item?.placeId ?? item?.place_id ?? item?.id ?? "";

      const description =
        item?.description ??
        item?.address ??
        item?.formatted_address ??
        item?.text ??
        item?.name ??
        "";

      return {
        placeId: normalizeString(placeId),
        description: normalizeString(description),
        raw: item,
      };
    })
    .filter((item) => item.placeId && item.description);
};

// =========================================================
// MAP LOCATION PICKER
// =========================================================

export default function MapLocationPicker({
  isOpen,
  onClose,
  onConfirm,
  apiKey,
  initialLatitude,
  initialLongitude,
}) {
  const mapContainerRef = useRef(null);

  const {
    placesAutoCompletionApi,
    getLatLongByPlaceIdApi,
    placesAutoCompletionLoading,
    getLatLongByPlaceIdLoading,
  } = useGoogleLocationApi();

const mapRef = useRef(null);
const geocoderRef = useRef(null);

const mapSearchInputRef = useRef(null);

const isProgrammaticMoveRef = useRef(false);
const reverseGeocodeRequestIdRef = useRef(0);

  const searchTimeoutRef = useRef(null);

  const searchRequestIdRef = useRef(0);

  const [isLoading, setIsLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const [selectedAddress, setSelectedAddress] = useState("");

  const [selectedLocation, setSelectedLocation] = useState(null);

  const [isMapMoving, setIsMapMoving] = useState(false);

  // =========================================================
  // MAP SEARCH
  // =========================================================

  const [mapSearchText, setMapSearchText] = useState("");

  const [mapSearchError, setMapSearchError] = useState("");

  const [mapSearchSuggestions, setMapSearchSuggestions] = useState([]);

  const [showMapSearchSuggestions, setShowMapSearchSuggestions] =
    useState(false);

  const isMapSearching =
    placesAutoCompletionLoading || getLatLongByPlaceIdLoading;

  // =========================================================
  // SEARCH INPUT
  // =========================================================

  const handleMapSearchChange = (event) => {
    const value = event.target.value;

    setMapSearchText(value);

    setMapSearchError("");

    if (searchTimeoutRef.current) {
      window.clearTimeout(searchTimeoutRef.current);
    }

    const query = value.trim();

    if (query.length < 3) {
      setMapSearchSuggestions([]);

      setShowMapSearchSuggestions(false);

      return;
    }

    searchTimeoutRef.current = window.setTimeout(async () => {
      try {
        const latitude = selectedLocation?.latitude ?? initialLatitude ?? 0;

        const longitude = selectedLocation?.longitude ?? initialLongitude ?? 0;

        const result = await placesAutoCompletionApi({
          input: query,

          latitude: Number(latitude || 0),

          longitude: Number(longitude || 0),

          radius: 50000,
        });

        const suggestions = extractPlaceSuggestions(result);

        setMapSearchSuggestions(suggestions);

        setShowMapSearchSuggestions(suggestions.length > 0);

        if (suggestions.length === 0) {
          setMapSearchError("No location found.");
        }
      } catch (error) {
        console.error("Map autocomplete error:", error);

        setMapSearchSuggestions([]);

        setShowMapSearchSuggestions(false);

        setMapSearchError("Unable to search location.");
      }
    }, 350);
  };

  

  // =========================================================
  // SEARCH SUGGESTION SELECT
  // =========================================================

const handleMapSuggestionSelect = async (suggestion) => {
  if (!suggestion?.placeId) {
    return;
  }

  mapSearchInputRef.current?.blur();

  if (searchTimeoutRef.current) {
    window.clearTimeout(searchTimeoutRef.current);
  }

  const selectedAddressText = normalizeString(suggestion.description);

  // Dropdown close
  setMapSearchText(selectedAddressText);
  setMapSearchSuggestions([]);
  setShowMapSearchSuggestions(false);
  setMapSearchError("");

  try {
    // =====================================================
    // 1. GET LAT / LNG FROM SELECTED PLACE ID
    // =====================================================

    const result = await getLatLongByPlaceIdApi({
      placeId: suggestion.placeId,
    });

    console.log("GET LAT LONG RESPONSE:", result);

    const locationData = result?.response ?? result?.data ?? result;

    const locationItem = Array.isArray(locationData)
      ? locationData[0]
      : locationData;

    const latitude = Number(
      locationItem?.latitude ??
        locationItem?.lat ??
        locationItem?.location?.latitude ??
        locationItem?.location?.lat,
    );

    const longitude = Number(
      locationItem?.longitude ??
        locationItem?.lontitude ??
        locationItem?.lng ??
        locationItem?.location?.longitude ??
        locationItem?.location?.lontitude ??
        locationItem?.location?.lng,
    );

    console.log("SELECTED LAT LNG:", {
      latitude,
      longitude,
    });

    if (
      !Number.isFinite(latitude) ||
      !Number.isFinite(longitude) ||
      (latitude === 0 && longitude === 0)
    ) {
      throw new Error("Invalid selected location coordinates.");
    }

    const newPosition = {
      lat: latitude,
      lng: longitude,
    };

    // =====================================================
    // 2. STORE SELECTED LOCATION
    // =====================================================

    setSelectedLocation({
      latitude,
      longitude,
    });

    // Jo suggestion user ne select kiya hai,
    // wahi niche Selected Address me show hoga.
    setSelectedAddress(selectedAddressText);

    // Search field me bhi same selected address.
    setMapSearchText(selectedAddressText);

    setMapSearchError("");

    // =====================================================
    // 3. MOVE MAP TO SELECTED LOCATION
    // =====================================================

    if (mapRef.current) {
      const map = mapRef.current;

      isProgrammaticMoveRef.current = true;
      setIsMapMoving(true);

      map.panTo(newPosition);
      map.setZoom(18);

      if (window.google?.maps?.event) {
        window.google.maps.event.addListenerOnce(map, "idle", () => {
          isProgrammaticMoveRef.current = false;
          setIsMapMoving(false);
        });
      } else {
        window.setTimeout(() => {
          isProgrammaticMoveRef.current = false;
          setIsMapMoving(false);
        }, 300);
      }
    }

    console.log("MAP LOCATION SELECTED:", {
      address: selectedAddressText,
      placeId: suggestion.placeId,
      latitude,
      longitude,
    });
  } catch (error) {
    console.error("MAP LOCATION SELECT ERROR:", error);

    setIsMapMoving(false);
    isProgrammaticMoveRef.current = false;

    setMapSearchError(error?.message || "Unable to select this location.");
  }
};

  // =========================================================
  // CLEANUP SEARCH
  // =========================================================

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        window.clearTimeout(searchTimeoutRef.current);
      }

      searchRequestIdRef.current += 1;
    };
  }, []);

  // =========================================================
  // INITIALIZE GOOGLE MAP
  // =========================================================

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let cancelled = false;

    const initializeMap = async () => {
      setIsLoading(true);

      setErrorMessage("");

      try {
        await loadGoogleMaps(apiKey);

        if (cancelled || !mapContainerRef.current) {
          return;
        }

        const fallbackLatitude = 28.6139;

        const fallbackLongitude = 77.209;

        let latitude = getValidCoordinate(initialLatitude, fallbackLatitude);

        let longitude = getValidCoordinate(initialLongitude, fallbackLongitude);

        // Old invalid saved location

        if (latitude === 0 && longitude === 0) {
          latitude = fallbackLatitude;

          longitude = fallbackLongitude;
        }

        const initialPosition = {
          lat: latitude,
          lng: longitude,
        };

        // ===================================================
        // CREATE GOOGLE MAP
        // ===================================================

        const map = new window.google.maps.Map(mapContainerRef.current, {
          center: initialPosition,

          zoom:
            latitude === fallbackLatitude && longitude === fallbackLongitude
              ? 11
              : 16,

          streetViewControl: false,

          mapTypeControl: false,

          // Remove pan + zoom camera control
          cameraControl: false,

          // Remove fullscreen button
          fullscreenControl: false,

          clickableIcons: false,

          gestureHandling: "greedy",
        });

        // ===================================================
        // GEOCODER
        // ===================================================

        const geocoder = new window.google.maps.Geocoder();

        mapRef.current = map;

        geocoderRef.current = geocoder;

        // ===================================================
        // FALLBACK REVERSE GEOCODER
        // ===================================================

        const getFallbackAddressFromCoordinates = async (
          latitude,
          longitude,
        ) => {
          try {
            const params = new URLSearchParams({
              lat: String(latitude),

              lon: String(longitude),

              format: "jsonv2",

              addressdetails: "1",

              zoom: "18",
            });

            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?${params.toString()}`,
              {
                method: "GET",

                headers: {
                  Accept: "application/json",

                  "Accept-Language": "en",
                },
              },
            );

            if (!response.ok) {
              return "";
            }

            const data = await response.json();

            return String(data?.display_name || "").trim();
          } catch (error) {
            console.error("Fallback reverse geocoding error:", error);

            return "";
          }
        };

        // ===================================================
        // UPDATE LOCATION
        // ===================================================

     const updateLocation = (position) => {
       const currentLatitude =
         typeof position.lat === "function" ? position.lat() : position.lat;

       const currentLongitude =
         typeof position.lng === "function" ? position.lng() : position.lng;

       const requestId = ++reverseGeocodeRequestIdRef.current;

       setSelectedLocation({
         latitude: currentLatitude,
         longitude: currentLongitude,
       });

       setSelectedAddress("Finding exact address...");

       geocoder.geocode(
         {
           location: {
             lat: currentLatitude,
             lng: currentLongitude,
           },
         },

         async (results, status) => {
           // Component close ho gaya ya newer request aa chuki hai
           if (cancelled || requestId !== reverseGeocodeRequestIdRef.current) {
             return;
           }

           if (
             status === "OK" &&
             Array.isArray(results) &&
             results.length > 0
           ) {
             const exactResult =
               results.find((item) =>
                 item?.types?.some((type) =>
                   [
                     "street_address",
                     "premise",
                     "subpremise",
                     "establishment",
                     "point_of_interest",
                     "route",
                   ].includes(type),
                 ),
               ) || results[0];

             const googleAddress = String(
               exactResult?.formatted_address || "",
             ).trim();

             if (requestId !== reverseGeocodeRequestIdRef.current) {
               return;
             }

             if (googleAddress) {
               setSelectedAddress(googleAddress);
               setMapSearchText(googleAddress);
               setMapSearchError("");
               return;
             }
           }

           const fallbackAddress = await getFallbackAddressFromCoordinates(
             currentLatitude,
             currentLongitude,
           );

           if (cancelled || requestId !== reverseGeocodeRequestIdRef.current) {
             return;
           }

           if (fallbackAddress) {
             setSelectedAddress(fallbackAddress);
             setMapSearchText(fallbackAddress);
             setMapSearchError("");
             return;
           }

           setSelectedAddress("Selected location");
           setMapSearchText("");
         },
       );
     };

        // ===================================================
        // INITIAL LOCATION
        // ===================================================

        updateLocation(initialPosition);

        // ===================================================
        // FIXED PIN + MAP MOVEMENT
        // ===================================================

     map.addListener("dragstart", () => {
       setIsMapMoving(true);
     });

   map.addListener("dragend", () => {
     const center = map.getCenter();

     setIsMapMoving(false);

     if (!center) {
       return;
     }

     // User manually map move kar chuka hai,
     // ab selected searched address lock nahi rahega.
     isProgrammaticMoveRef.current = false;

     updateLocation(center);
   });
      } catch (error) {
        console.error("Google map error:", error);

        setErrorMessage(error?.message || "Unable to load map.");
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    initializeMap();

    return () => {
      cancelled = true;

      if (window.google?.maps && mapRef.current) {
        window.google.maps.event.clearInstanceListeners(mapRef.current);
      }

      mapRef.current = null;

      geocoderRef.current = null;
    };
  }, [isOpen, apiKey, initialLatitude, initialLongitude]);

  // =========================================================
  // CLOSED
  // =========================================================

  if (!isOpen) {
    return null;
  }

  // =========================================================
  // CONFIRM
  // =========================================================

  const handleConfirm = () => {
    const finalAddress = String(selectedAddress || "").trim();

    if (
      !selectedLocation ||
      !finalAddress ||
      finalAddress === "Finding exact address..." ||
      finalAddress === "Unable to find exact address for this location."
    ) {
      return;
    }

    onConfirm?.({
      address: finalAddress,

      latitude: selectedLocation.latitude,

      longitude: selectedLocation.longitude,
    });
  };

  // =========================================================
  // UI
  // =========================================================

  return (
    <div
      className="
        fixed
        inset-0
        z-[100000]

        flex
        items-end
        justify-center

        overflow-hidden

        bg-black/65
        p-0

        backdrop-blur-[1px]

        sm:items-center
        sm:p-4

        lg:p-6
      "
    >
      {/* =================================================== */}
      {/* MODAL */}
      {/* =================================================== */}

      <div
        className="
          flex
          h-[100dvh]
          w-full
          min-w-0
          flex-col
          overflow-hidden

          bg-white

          shadow-2xl

          sm:h-auto
          sm:max-h-[calc(100dvh-32px)]
          sm:max-w-[760px]
          sm:rounded-[20px]

          md:max-w-[840px]

          lg:max-w-[900px]
          lg:rounded-[24px]
        "
      >
        {/* ================================================= */}
        {/* HEADER */}
        {/* ================================================= */}

        <div
          className="
            flex
            shrink-0
            items-start
            justify-between
            gap-3

            border-b
            border-[#edf0f4]

            px-4
            py-3

            sm:px-5
            sm:py-4

            md:px-6
          "
        >
          <div className="min-w-0 flex-1">
            <h2
              className="
                text-[16px]
                font-semibold
                leading-5
                text-[#151d2d]

                min-[380px]:text-[17px]

                sm:text-[19px]
                sm:leading-6

                md:text-[20px]
              "
            >
              Select location on map
            </h2>

            <p
              className="
                mt-1

                max-w-[600px]

                text-[10px]
                leading-4
                text-[#708099]

                min-[380px]:text-[11px]

                sm:text-[12px]
                sm:leading-5

                md:text-[13px]
              "
            >
              Drag the pin or tap anywhere on the map to change the address.
            </p>
          </div>

          {/* CLOSE */}

          <button
            type="button"
            onClick={onClose}
            aria-label="Close map"
            className="
              flex
              h-8
              w-8
              shrink-0
              items-center
              justify-center

              rounded-full

              border
              border-[#dfe5ec]

              bg-white
              text-[#738298]

              transition

              hover:bg-[#f5f7f9]

              active:scale-95

              sm:h-9
              sm:w-9

              md:h-10
              md:w-10
            "
          >
            <X className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
          </button>
        </div>

        {/* ================================================= */}
        {/* MAP */}
        {/* ================================================= */}

        <div
          className="
            relative

            min-h-[220px]
            min-w-0
            flex-1

            sm:h-[390px]
            sm:min-h-[390px]
            sm:flex-none

            md:h-[460px]
            md:min-h-[460px]

            lg:h-[500px]
            lg:min-h-[500px]

            xl:h-[520px]
            xl:min-h-[520px]
          "
        >
          {/* =============================================== */}
          {/* GOOGLE MAP */}
          {/* =============================================== */}
          <div
            ref={mapContainerRef}
            className="
    absolute
    inset-0
    h-full
    w-full
    bg-[#eef1f4]
  "
          />

          {/* FIXED CENTER PIN */}
          <div
            className="
    pointer-events-none
    absolute
    left-1/2
    top-1/2
    z-20
    -translate-x-1/2
    -translate-y-full
  "
          >
            <img
              src="https://maps.gstatic.com/mapfiles/api-3/images/spotlight-poi3.png"
              alt=""
              draggable={false}
              className="
      h-[43px]
      w-[27px]
      select-none
      object-contain
      drop-shadow-[0_3px_3px_rgba(0,0,0,0.35)]
    "
            />
          </div>

          {/* =============================================== */}
          {/* SEARCH BAR ON MAP */}
          {/* =============================================== */}

          <div
            className="
  absolute left-3 right-3 top-1

  z-30

  sm:left-5
  sm:right-5
  sm:top-2

  md:left-6
  md:right-6
"
          >
            {/* SEARCH INPUT */}

            <div
              className={`
                flex
                h-11
                w-full
                min-w-0
                items-center
                gap-2.5

                rounded-[12px]

                border

                bg-white/95

                px-3

                shadow-[0_6px_24px_rgba(15,23,42,0.16)]

                backdrop-blur-md

                transition

                focus-within:bg-white

                sm:h-12
                sm:gap-3
                sm:px-4

                ${
                  mapSearchError
                    ? "border-red-300 focus-within:border-red-400"
                    : "border-white/80 focus-within:border-[#08a66c]"
                }
              `}
            >
              {/* SEARCH ICON */}

              <Search
                className="
                  h-[17px]
                  w-[17px]
                  shrink-0

                  text-[#7d8ca0]

                  sm:h-[19px]
                  sm:w-[19px]
                "
              />

              {/* INPUT */}

              <input
                ref={mapSearchInputRef}
                type="text"
                value={mapSearchText}
                onChange={handleMapSearchChange}
                onFocus={() => {
                  if (mapSearchSuggestions.length > 0) {
                    setShowMapSearchSuggestions(true);
                  }
                }}
                placeholder="Search area, street, landmark..."
                autoComplete="off"
                spellCheck={false}
                className="
                  h-full
                  min-w-0
                  flex-1

                  bg-transparent

                  text-[13px]
                  text-[#344054]

                  outline-none

                  placeholder:text-[#98a2b3]

                  sm:text-[14px]
                "
              />

              {/* SEARCH LOADER */}

              {isMapSearching && (
                <Loader2
                  className="
                    h-[17px]
                    w-[17px]
                    shrink-0

                    animate-spin

                    text-[#08a66c]
                  "
                />
              )}

              {/* CLEAR */}

              {mapSearchText && !isMapSearching && (
                <button
                  type="button"
                  onClick={() => {
                    if (searchTimeoutRef.current) {
                      window.clearTimeout(searchTimeoutRef.current);
                    }

                    setMapSearchText("");

                    setMapSearchSuggestions([]);

                    setShowMapSearchSuggestions(false);

                    setMapSearchError("");
                  }}
                  aria-label="Clear map search"
                  className="
                      flex
                      h-7
                      w-7
                      shrink-0
                      items-center
                      justify-center

                      rounded-full

                      text-[#8b98aa]

                      transition

                      hover:bg-[#edf1f5]
                      hover:text-[#344054]
                    "
                >
                  <X size={15} />
                </button>
              )}
            </div>

            {/* ============================================= */}
            {/* SEARCH SUGGESTIONS */}
            {/* ============================================= */}

            {showMapSearchSuggestions && mapSearchSuggestions.length > 0 && (
              <div
                className="
                    absolute

                    left-0
                    right-0
                    top-full

                    z-50

                    mt-2

                    max-h-[240px]

                    overflow-y-auto

                    rounded-[12px]

                    border
                    border-[#dce3ea]

                    bg-white

                    shadow-[0_12px_32px_rgba(15,23,42,0.18)]

                    [scrollbar-width:none]
                    [-ms-overflow-style:none]

                    [&::-webkit-scrollbar]:hidden
                  "
              >
                {mapSearchSuggestions.map((suggestion) => (
                  <button
                    key={suggestion.placeId}
                    type="button"
                    disabled={isMapSearching}
                    onClick={() => handleMapSuggestionSelect(suggestion)}
                    className="
                          flex
                          w-full
                          items-start
                          gap-3

                          border-b
                          border-[#edf0f3]

                          px-3
                          py-3

                          text-left

                          transition

                          last:border-b-0

                          hover:bg-[#f5faf8]

                          disabled:cursor-not-allowed
                          disabled:opacity-60

                          sm:px-4
                        "
                  >
                    <div
                      className="
                            mt-0.5

                            flex
                            h-8
                            w-8
                            shrink-0
                            items-center
                            justify-center

                            rounded-full

                            bg-[#eaf8f3]

                            text-[#08a66c]
                          "
                    >
                      <MapPin size={15} />
                    </div>

                    <span
                      className="
                            min-w-0
                            flex-1

                            text-[12px]
                            leading-5
                            text-[#344054]

                            sm:text-[13px]
                          "
                    >
                      {suggestion.description}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* ============================================= */}
            {/* SEARCH ERROR */}
            {/* ============================================= */}

            {mapSearchError && (
              <div
                className="
                  mt-2

                  inline-flex

                  rounded-[8px]

                  bg-white/95

                  px-3
                  py-1.5

                  text-[10px]
                  font-medium
                  text-red-500

                  shadow-md

                  backdrop-blur-md

                  sm:text-[11px]
                "
              >
                {mapSearchError}
              </div>
            )}

            {/* ============================================= */}
            {/* MINIMUM CHARACTERS MESSAGE */}
            {/* ============================================= */}

            {!mapSearchError &&
              mapSearchText.trim().length > 0 &&
              mapSearchText.trim().length < 3 && (
                <div
                  className="
                    mt-2

                    inline-flex

                    rounded-[8px]

                    bg-white/95

                    px-3
                    py-1.5

                    text-[10px]
                    text-[#667085]

                    shadow-md

                    backdrop-blur-md

                    sm:text-[11px]
                  "
                >
                  Type at least 3 characters to search.
                </div>
              )}
          </div>

          {/* =============================================== */}
          {/* MAP LOADER */}
          {/* =============================================== */}

          {isLoading && (
            <div
              className="
                absolute
                inset-0

                z-20

                flex
                items-center
                justify-center

                bg-white/70

                px-4

                backdrop-blur-[1px]
              "
            >
              <div
                className="
                  flex
                  items-center
                  gap-2

                  rounded-xl

                  bg-white

                  px-3
                  py-2.5

                  text-[11px]
                  font-medium
                  text-[#5d6c80]

                  shadow-lg

                  sm:px-4
                  sm:py-3
                  sm:text-sm
                "
              >
                <Loader2
                  size={17}
                  className="shrink-0 animate-spin text-[#08a66c]"
                />
                Loading Google Map...
              </div>
            </div>
          )}

          {/* =============================================== */}
          {/* ERROR */}
          {/* =============================================== */}

          {errorMessage && (
            <div
              className="
                absolute
                inset-0

                z-20

                flex
                items-center
                justify-center

                bg-white

                px-5

                text-center

                sm:px-6
              "
            >
              <div className="max-w-[360px]">
                <MapPin
                  size={30}
                  className="mx-auto text-red-400 sm:h-8 sm:w-8"
                />

                <p className="mt-3 text-[13px] font-semibold text-[#303846] sm:text-sm">
                  Map could not be loaded
                </p>

                <p className="mt-1 text-[11px] leading-5 text-[#8390a2] sm:text-xs">
                  {errorMessage}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ================================================= */}
        {/* SELECTED ADDRESS */}
        {/* ================================================= */}

        <div
          className="
            shrink-0

            border-t
            border-[#edf0f4]

            bg-white

            px-3
            pt-3

            sm:px-5
            sm:pt-4

            md:px-6
          "
        >
          <div
            className="
              flex
              min-w-0
              items-start
              gap-2.5

              rounded-[11px]

              border
              border-[#dfe5ec]

              bg-[#f8fafc]

              px-3
              py-3

              sm:gap-3

              sm:rounded-[13px]

              sm:px-4
              sm:py-4
            "
          >
            <div
              className="
                flex
                h-8
                w-8
                shrink-0
                items-center
                justify-center

                rounded-full

                bg-[#eaf8f3]

                text-[#08a66c]

                sm:h-9
                sm:w-9
              "
            >
              <MapPin className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
            </div>

            <div className="min-w-0 flex-1">
              <p
                className="
                  text-[9px]
                  font-semibold
                  uppercase
                  tracking-wide

                  text-[#77879c]

                  sm:text-[10px]
                "
              >
                Selected address
              </p>

              <p
                className="
                  mt-1

                  line-clamp-2

                  break-words

                  text-[11px]
                  leading-[17px]
                  text-[#354055]

                  sm:text-[13px]
                  sm:leading-5

                  md:line-clamp-3
                "
              >
                {selectedAddress || "Move the pin to select an address"}
              </p>
            </div>
          </div>
        </div>

        {/* ================================================= */}
        {/* BOTTOM ACTIONS */}
        {/* ================================================= */}

        <div
          className="
            grid
            shrink-0
            grid-cols-1
            gap-2

            bg-white

            px-3
            py-3

            pb-[max(12px,env(safe-area-inset-bottom))]

            min-[340px]:grid-cols-2

            sm:gap-3
            sm:px-5
            sm:py-4

            md:px-6
          "
        >
          {/* =============================================== */}
          {/* CANCEL */}
          {/* =============================================== */}

          <button
            type="button"
            onClick={onClose}
            className="
              flex
              h-10
              w-full
              items-center
              justify-center

              rounded-[10px]

              border
              border-[#dce3ea]

              bg-white

              px-3

              text-[12px]
              font-semibold
              text-[#425066]

              transition

              hover:bg-[#f7f8fa]

              active:scale-[0.99]

              sm:h-11
              sm:text-[13px]
            "
          >
            Cancel
          </button>

          {/* =============================================== */}
          {/* CONFIRM */}
          {/* =============================================== */}

          <button
            type="button"
            onClick={handleConfirm}
            disabled={
              !selectedLocation ||
              isLoading ||
              isMapMoving ||
              Boolean(errorMessage) ||
              !selectedAddress ||
              selectedAddress === "Finding exact address..." ||
              selectedAddress ===
                "Unable to find exact address for this location."
            }
            className="
              flex
              h-10
              w-full
              items-center
              justify-center
              gap-1.5

              rounded-[10px]

              bg-[#65c9a7]

              px-3

              text-[12px]
              font-semibold
              text-white

              transition

              hover:bg-[#32916f]

              active:scale-[0.99]

              disabled:cursor-not-allowed
              disabled:opacity-50

              sm:h-11
              sm:gap-2
              sm:text-[13px]
            "
          >
            {selectedAddress === "Finding exact address..." ? (
              <>
                <Loader2 size={15} className="shrink-0 animate-spin" />

                <span className="truncate">Finding address...</span>
              </>
            ) : (
              <>
                <Check size={15} className="shrink-0 sm:h-4 sm:w-4" />

                <span className="truncate">Confirm location</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
